import { FormBuilder, Validators } from '@angular/forms';
import {
    CloudAppSecuritySessionControlType,
    ConditionalAccessApplications,
    ConditionalAccessClientApp,
    ConditionalAccessDevicePlatform,
    ConditionalAccessGrantControl,
    ConditionalAccessLocations,
    ContinuousAccessEvaluationMode,
    RiskLevel,
    SignInFrequencyAuthenticationType,
    SignInFrequencyInterval,
    SigninFrequencyType,
} from '@microsoft/microsoft-graph-types-beta';
import {
    getArrayListFromStringListPatters,
    getExcludeGuestsOrExternalUsersValue,
    getExcludeUsersItemsFromSchema,
    getIncludeGuestsOrExternalUsersValue,
    getIncludeUsersConfig,
    getIncludeUsersItemsFromSchema,
    getUserItemsFromSchema,
} from './utlize';
import { GenericCapSchema_Data } from 'projects/angular-clarity/src/app/modules/sway/models/baseline-schemas/generic-cap-schema-model';
import { ArrayTypeSchema } from 'projects/angular-clarity/src/app/modules/sway/models/schema';

export interface GrantControlsController {
    builtInControls: ConditionalAccessGrantControl[];
    operator: 'or' | 'and';
    authenticationStrength: string | null;
}

export interface SessionControlsControllerValues {
    disableResilienceDefaults: boolean;
    continuousAccessEvaluation: {
        mode: ContinuousAccessEvaluationMode;
    };
    signInFrequency: {
        isEnabled: boolean;
        type: SigninFrequencyType;
        value: number;
        frequencyInterval: SignInFrequencyInterval;
        authenticationType: SignInFrequencyAuthenticationType;
    };

    cloudAppSecurity: {
        isEnabled: boolean;
        cloudAppSecurityType: CloudAppSecuritySessionControlType;
    };

    persistentBrowser: {
        isEnabled: boolean;
        mode: 'never' | 'always';
    };
}

interface UserSettings {
    includeGuestsOrExternalUsers: boolean;
    includeRoles: boolean;
    includeUsers: boolean;
    includeGroups: boolean;
}

interface ExcludeUserSettings {
    excludeGuestsOrExternalUsers: boolean;
    excludeRoles: boolean;
    excludeUsers: boolean;
    excludeGroups: boolean;
}

export interface ConditionalAccessPolicyForm {
    state: 'enabled';
    disabled;
    enabledForReportingButNotEnforced;
    displayName: string;
    grant: GrantControlsController;
    sessionControls: SessionControlsControllerValues;
    includeUsersConfig: 'All' | 'None' | 'GuestsOrExternalUsers';
    includeUserSettings: UserSettings;
    excludeUserSettings: ExcludeUserSettings;
    includeRoles: string[];
    excludeRoles: string[];
    includeUsers: string[];
    excludeUsers: string[];
    includeGroups: string[];
    excludeGroups: string[];
    includeGuestsOrExternalUsers: string[];
    excludeGuestsOrExternalUsers: string[];

    conditions: FormConditions;
}

export interface FormConditions {
    userRiskLevels: RiskLevel[];
    signInRiskLevels: RiskLevel[];
    insiderRiskLevels: string[];
    clientAppTypes: ConditionalAccessClientApp[];
    applications: ConditionalAccessApplications;
    platforms: {
        includePlatforms: ConditionalAccessDevicePlatform[];
        excludePlatforms: ConditionalAccessDevicePlatform[];
    };

    locations: ConditionalAccessLocations;

    authenticationFlows: {
        transferMethods: string[];
    };
}

interface NoCapSchema {
    type: 'array';
    not: {
        contains: {
            type: 'object';
            properties: {
                displayName: {
                    type: 'string';
                    const: string;
                };
            };
        };
    };
}

export function initCapGenericForm(
    schema: ArrayTypeSchema<GenericCapSchema_Data> & NoCapSchema,
    fb: FormBuilder,
    name: string,
) {
    const isNoPolicySchema = !!schema?.not?.contains;

    if (isNoPolicySchema) {
        return fb.group({
            state: ['delete'],
            displayName: [schema?.not?.contains?.properties?.displayName?.const || name, Validators.required],

            grant: fb.group({
                builtInControls: [[]],
                authenticationStrength: [],
                operator: [],
            }),

            sessionControls: fb.group({
                disableResilienceDefaults: [],

                continuousAccessEvaluation: fb.group({
                    mode: [],
                }),

                signInFrequency: fb.group({
                    isEnabled: [],
                    type: [],
                    value: [],
                    frequencyInterval: [],
                    authenticationType: [],
                }),

                cloudAppSecurity: fb.group({
                    isEnabled: [],
                    cloudAppSecurityType: [],
                }),
            }),
            conditions: fb.group({
                userRiskLevels: [[]],
                insiderRiskLevels: [[]],
                signInRiskLevels: [[]],

                platforms: fb.group({
                    includePlatforms: [[]],
                    excludePlatforms: [[]],
                }),

                authenticationFlows: fb.group({
                    transferMethods: [[]],
                }),

                clientAppTypes: [[]],

                locations: fb.group({
                    includeLocations: [[]],
                    excludeLocations: [[]],
                }),

                applications: fb.group({
                    includeApplications: [[]],
                }),
            }),

            includeRoles: [[]],
            excludeRoles: [[]],
            includeUsers: [[]],
            excludeUsers: [[]],
            includeGroups: [[]],
            excludeGroups: [[]],
            includeUsersConfig: ['All'],
            includeGuestsOrExternalUsers: [[]],
            excludeGuestsOrExternalUsers: [[]],

            includeUserSettings: fb.group({
                includeGuestsOrExternalUsers: [false],
                includeRoles: [false],
                includeUsers: [false],
                includeGroups: [false],
            }),

            excludeUserSettings: fb.group({
                excludeGuestsOrExternalUsers: [false],
                excludeRoles: [false],
                excludeUsers: [false],
                excludeGroups: [false],
            }),
        });
    } else {
        return fb.group({
            state: [schema?.contains?.properties?.state?.const || 'enabledForReportingButNotEnforced'],
            displayName: [schema?.contains?.properties?.displayName?.const || name, Validators.required],

            grant: fb.group({
                builtInControls: [
                    schema?.contains?.properties?.grantControls?.properties?.builtInControls?.items?.enum || [],
                ],
                operator: [schema?.contains?.properties?.grantControls?.properties?.operator?.const],
                authenticationStrength: [
                    schema?.contains?.properties?.grantControls?.properties?.authenticationStrength?.properties?.id
                        ?.const,
                ],
            }),

            sessionControls: fb.group({
                disableResilienceDefaults: [
                    schema?.contains?.properties?.sessionControls?.properties?.disableResilienceDefaults?.const,
                ],

                continuousAccessEvaluation: fb.group({
                    mode: [
                        schema?.contains?.properties?.sessionControls?.properties?.continuousAccessEvaluation
                            ?.properties?.mode?.const,
                    ],
                }),

                signInFrequency: fb.group({
                    isEnabled: [
                        schema?.contains?.properties?.sessionControls?.properties?.signInFrequency?.properties
                            ?.isEnabled?.const,
                    ],
                    type: [
                        schema?.contains?.properties?.sessionControls?.properties?.signInFrequency?.properties?.type
                            ?.const,
                    ],
                    value: [
                        schema?.contains?.properties?.sessionControls?.properties?.signInFrequency?.properties?.value
                            ?.const,
                    ],
                    frequencyInterval: [
                        schema?.contains?.properties?.sessionControls?.properties?.signInFrequency?.properties
                            ?.frequencyInterval?.const,
                    ],
                    authenticationType: [
                        schema?.contains?.properties?.sessionControls?.properties?.signInFrequency?.properties
                            ?.authenticationType?.const,
                    ],
                }),

                cloudAppSecurity: fb.group({
                    isEnabled: [
                        schema?.contains?.properties?.sessionControls?.properties?.cloudAppSecurity?.properties
                            ?.isEnabled?.const,
                    ],
                    cloudAppSecurityType: [
                        schema?.contains?.properties?.sessionControls?.properties?.cloudAppSecurity?.properties
                            ?.cloudAppSecurityType?.const,
                    ],
                }),
                persistentBrowser: fb.group({
                    isEnabled: [
                        schema?.contains?.properties?.sessionControls?.properties?.persistentBrowser?.properties
                            ?.isEnabled?.const,
                    ],
                    mode: [
                        schema?.contains?.properties?.sessionControls?.properties?.persistentBrowser?.properties?.mode
                            ?.const,
                    ],
                }),
            }),

            conditions: fb.group({
                userRiskLevels: [
                    schema?.contains?.properties?.conditions?.properties?.userRiskLevels?.items?.enum || [],
                ],
                signInRiskLevels: [
                    schema?.contains?.properties?.conditions?.properties?.signInRiskLevels?.items?.enum || [],
                ],
                insiderRiskLevels: [
                    getArrayListFromStringListPatters(
                        schema?.contains?.properties?.conditions?.properties?.insiderRiskLevels?.pattern,
                    ),
                ],

                platforms: fb.group({
                    includePlatforms: [
                        schema?.contains?.properties?.conditions?.properties?.platforms?.properties?.includePlatforms
                            ?.items?.enum || [],
                    ],
                    excludePlatforms: [
                        schema?.contains?.properties?.conditions?.properties?.platforms?.properties?.excludePlatforms
                            ?.items?.enum || [],
                    ],
                }),
                applications: fb.group({
                    includeApplications: [
                        schema?.contains?.properties?.conditions?.properties?.applications?.properties
                            ?.includeApplications?.items?.enum || ['None'],
                    ],
                }),
                locations: fb.group({
                    includeLocations: [
                        schema?.contains?.properties?.conditions?.properties?.locations?.properties?.includeLocations
                            ?.items?.enum || [],
                    ],
                    excludeLocations: [
                        schema?.contains?.properties?.conditions?.properties?.locations?.properties?.excludeLocations
                            ?.items?.enum || [],
                    ],
                }),

                clientAppTypes: [
                    schema?.contains?.properties?.conditions?.properties?.clientAppTypes?.items?.enum || [],
                ],
                authenticationFlows: fb.group({
                    transferMethods: [
                        getArrayListFromStringListPatters(
                            schema?.contains?.properties?.conditions?.properties?.authenticationFlows?.properties
                                ?.transferMethods?.pattern,
                        ),
                    ],
                }),
            }),

            includeUsersConfig: [getIncludeUsersConfig(schema)], // all, none, else

            includeUserSettings: fb.group({
                includeGuestsOrExternalUsers: [getIncludeGuestsOrExternalUsersValue(schema)?.length > 0],
                includeRoles: [getUserItemsFromSchema(schema, 'includeRoles')?.length > 0],
                includeUsers: [getIncludeUsersItemsFromSchema(schema)?.length > 0],
                includeGroups: [getUserItemsFromSchema(schema, 'includeGroups')?.length > 0],
            }),

            excludeUserSettings: fb.group({
                excludeGuestsOrExternalUsers: [getExcludeGuestsOrExternalUsersValue(schema)?.length > 0],
                excludeRoles: [getUserItemsFromSchema(schema, 'excludeRoles')?.length > 0],
                excludeUsers: [getExcludeUsersItemsFromSchema(schema)?.length > 0],
                excludeGroups: [getUserItemsFromSchema(schema, 'excludeGroups')?.length > 0],
            }),

            // grantControl : fb.group({
            //     builtInControls: [getbuiltInControlsFromSchema(schema)]
            // }),

            includeRoles: [getUserItemsFromSchema(schema, 'includeRoles')],
            excludeRoles: [getUserItemsFromSchema(schema, 'excludeRoles')],

            includeUsers: [getIncludeUsersItemsFromSchema(schema)],
            excludeUsers: [getUserItemsFromSchema(schema, 'excludeUsers')],

            includeGroups: [getUserItemsFromSchema(schema, 'includeGroups')],
            excludeGroups: [getUserItemsFromSchema(schema, 'excludeGroups')],

            includeGuestsOrExternalUsers: [getIncludeGuestsOrExternalUsersValue(schema)],

            excludeGuestsOrExternalUsers: [getExcludeGuestsOrExternalUsersValue(schema)],
        });
    }
}

export function checkIsNoSessionOrGrantControl(form: ConditionalAccessPolicyForm) {
    const isNoGrant = form.grant.builtInControls?.length === 0 && !form.grant.authenticationStrength;
    const isNoSession =
        !form.sessionControls.cloudAppSecurity.isEnabled &&
        !form.sessionControls.continuousAccessEvaluation.mode &&
        !form.sessionControls.disableResilienceDefaults &&
        !form.sessionControls.signInFrequency.isEnabled;

    return isNoGrant && isNoSession;
}

export function checkAnyConditionWithSeiCustomizeContinuousAccessDisabled(form: ConditionalAccessPolicyForm) {
    const isCustomAccessEvaluationDisabled = form.sessionControls.continuousAccessEvaluation.mode === 'disabled';
    if (!isCustomAccessEvaluationDisabled) {
        return false;
    }

    const isAnyRestrictionsEnabled =
        form.conditions.authenticationFlows.transferMethods.length > 0 ||
        form.conditions.userRiskLevels.length > 0 ||
        form.conditions.signInRiskLevels.length > 0 ||
        form.conditions.insiderRiskLevels.length > 0 ||
        form.conditions.platforms.includePlatforms.length > 0 ||
        form.conditions.platforms.excludePlatforms.length > 0 ||
        form.conditions.locations.includeLocations.length > 0 ||
        form.conditions.locations.excludeLocations.length > 0;

    return isAnyRestrictionsEnabled;
}

export function checkIsPersistentBrowserSessionWithNoResource(formValues: ConditionalAccessPolicyForm) {
    const persistentBrowserSessionEnabled = formValues.sessionControls.persistentBrowser.isEnabled;

    if (!persistentBrowserSessionEnabled) {
        return false;
    }

    const isAllCloudApps = formValues.conditions.applications.includeApplications[0]?.toLocaleLowerCase() === 'all';

    return !isAllCloudApps;
}

export function checkIsNoIncludeUsers(formValue) {
    if (formValue.includeUsersConfig === 'GuestsOrExternalUsers') {
        if (
            (formValue.includeUsers?.length > 0 && formValue.includeUserSettings.includeUsers) ||
            (formValue.includeRoles?.length > 0 && formValue.includeUserSettings.includeRoles) ||
            (formValue.includeGuestsOrExternalUsers?.length > 0 &&
                formValue.includeUserSettings.includeGuestsOrExternalUsers) ||
            (formValue.includeGroups?.length > 0 && formValue.includeUserSettings.includeGroups)
        ) {
            return false;
        } else {
            return true;
        }
    } else {
        return false;
    }
}
